(() => {
  const configElement = document.getElementById('ot-commerce-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  const text = config.strings;
  const drawer = document.querySelector('[data-cart-drawer]');
  const requests = new WeakMap();
  const observers = new WeakMap();
  let cartBusy = false;
  let previousFocus;
  const money = value => {
    try { return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(value / 100); }
    catch { return `${(value / 100).toFixed(2)} ${config.currency}`; }
  };
  const element = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  };
  const responseJson = async response => {
    let data;
    try { data = await response.json(); } catch { throw new Error(text.error); }
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || text.error);
    return data;
  };
  const setProductBusy = (root, busy) => {
    root.toggleAttribute('aria-busy', busy);
    root.querySelectorAll('[data-add-button], [data-buy-now]').forEach(button => {
      if (busy) {
        button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
      } else {
        button.disabled = button.dataset.wasDisabled === 'true';
        delete button.dataset.wasDisabled;
      }
    });
  };
  const setMessage = (root, message, error = false) => {
    const output = root.querySelector('[data-product-message]');
    if (output) {
      output.textContent = message;
      output.classList.toggle('ot-message--error', error);
    }
  };
  const galleryTo = (root, index, behavior = 'smooth') => {
    const track = root.querySelector('[data-gallery-track]');
    if (!track) return;
    const count = track.children.length;
    const target = (index + count) % count;
    track.scrollTo({ left: track.clientWidth * target, behavior });
    root.dataset.galleryIndex = String(target);
    root.querySelectorAll('[data-gallery-to]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryTo) === target)));
  };
  const initializeProduct = root => {
    root.dataset.enhanced = 'true';
    const variantInput = root.querySelector('[data-variant-input]');
    const fallback = root.querySelector('[data-native-variant]');
    if (variantInput) variantInput.disabled = false;
    if (fallback) fallback.disabled = true;
    const track = root.querySelector('[data-gallery-track]');
    if (track) {
      const index = Array.from(track.children).findIndex(slide => slide.dataset.mediaId === root.dataset.featuredMedia);
      requestAnimationFrame(() => galleryTo(root, Math.max(index, 0), 'instant'));
      let scheduled = false;
      track.addEventListener('scroll', () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          root.dataset.galleryIndex = String(index);
          root.querySelectorAll('[data-gallery-to]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryTo) === index)));
        });
      }, { passive: true });
      const resize = new ResizeObserver(() => galleryTo(root, Number(root.dataset.galleryIndex || 0), 'instant'));
      resize.observe(track);
      observers.set(root, [resize]);
    }
    const buyRow = root.querySelector('[data-buy-row]');
    const sticky = root.querySelector('[data-sticky-cart]');
    if (buyRow && sticky && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        sticky.hidden = entries[0].isIntersecting || entries[0].boundingClientRect.top >= 0;
      });
      observer.observe(buyRow);
      observers.set(root, [...(observers.get(root) || []), observer]);
    }
  };
  const selectVariant = async (root, change) => {
    requests.get(root)?.abort();
    const controller = new AbortController();
    requests.set(root, controller);
    const url = new URL(root.dataset.productUrl, window.location.origin);
    if (change.matches('[data-sticky-variant]')) url.searchParams.set('variant', change.value);
    else {
      const values = Array.from(root.querySelectorAll('[data-option-position]')).map(field => field.querySelector('input:checked')?.dataset.optionValueId || field.querySelector('select')?.value).filter(Boolean);
      url.searchParams.set('option_values', values.join(','));
    }
    const pageUrl = new URL(url);
    url.searchParams.set('section_id', root.dataset.sectionId);
    const focusId = change.id;
    const quantity = root.querySelector('[data-product-quantity]')?.value;
    setProductBusy(root, true);
    setMessage(root, text.loading);
    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(text.error);
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = documentFragment.querySelector('[data-product-root]');
      if (!replacement) throw new Error(text.error);
      const nextQuantity = replacement.querySelector('[data-product-quantity]');
      if (nextQuantity && quantity) {
        const min = Number(nextQuantity.min || 1);
        const max = Number(nextQuantity.max || Infinity);
        const step = Number(nextQuantity.step || 1);
        nextQuantity.value = String(Math.min(max, Math.max(min, min + Math.floor((Number(quantity) - min) / step) * step)));
      }
      observers.get(root)?.forEach(observer => observer.disconnect());
      root.replaceWith(replacement);
      initializeProduct(replacement);
      if (replacement.dataset.variantId) {
        pageUrl.searchParams.delete('option_values');
        pageUrl.searchParams.set('variant', replacement.dataset.variantId);
      }
      window.history.replaceState({}, '', pageUrl.pathname + pageUrl.search);
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      window.Shopify?.PaymentButton?.init();
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(pageUrl.pathname + pageUrl.search);
    }
  };
  const renderCart = cart => {
    if (!drawer) return;
    if (cart.currency) config.currency = cart.currency;
    document.querySelectorAll('[data-cart-count]').forEach(node => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
    const container = drawer.querySelector('[data-drawer-items]');
    container.replaceChildren();
    if (!cart.items.length) container.append(element('p', 'ot-cart-empty', text.empty));
    for (const item of cart.items) {
      const line = element('div', 'ot-drawer-line');
      line.dataset.lineKey = item.key;
      const imageLink = element('a', 'ot-drawer-image');
      imageLink.href = item.url;
      if (item.featured_image?.url || item.image) {
        const image = element('img');
        image.src = item.featured_image?.url || item.image;
        image.alt = item.featured_image?.alt || item.product_title;
        image.width = 100;
        image.height = 100;
        image.loading = 'lazy';
        imageLink.append(image);
      }
      const details = element('div', 'ot-drawer-details');
      const title = element('a', 'ot-drawer-title', item.product_title);
      title.href = item.url;
      details.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') details.append(element('p', 'ot-drawer-option', item.variant_title));
      for (const [key, value] of Object.entries(item.properties || {})) {
        if (!key.startsWith('_') && value) details.append(element('p', 'ot-drawer-option', `${key}: ${value}`));
      }
      if (item.selling_plan_allocation) details.append(element('p', 'ot-drawer-option', item.selling_plan_allocation.selling_plan.name));
      const price = element('p', 'ot-drawer-price');
      if (item.original_line_price > item.final_line_price) price.append(element('s', '', money(item.original_line_price)), document.createTextNode(' '));
      price.append(element('strong', '', money(item.final_line_price)));
      details.append(price);
      for (const discount of item.line_level_discount_allocations || []) details.append(element('p', 'ot-drawer-discount', discount.discount_application.title));
      const controls = element('div', 'ot-drawer-controls');
      const quantity = element('div', 'ot-quantity');
      const min = item.quantity_rule?.min || 1;
      const increment = item.quantity_rule?.increment || 1;
      const max = item.quantity_rule?.max;
      const decrease = element('button', '', '−');
      decrease.type = 'button';
      decrease.dataset.cartStep = String(-increment);
      decrease.setAttribute('aria-label', text.decrease);
      const input = element('input');
      input.type = 'number';
      input.value = item.quantity;
      input.min = min;
      input.step = increment;
      if (max) input.max = max;
      input.inputMode = 'numeric';
      input.dataset.cartQuantity = '';
      input.setAttribute('aria-label', `${text.quantity}: ${item.product_title}`);
      const increase = element('button', '', '+');
      increase.type = 'button';
      increase.dataset.cartStep = String(increment);
      increase.setAttribute('aria-label', text.increase);
      quantity.append(decrease, input, increase);
      const remove = element('button', 'ot-cart-remove', text.remove);
      remove.type = 'button';
      remove.dataset.cartRemove = '';
      controls.append(quantity, remove);
      details.append(controls);
      line.append(imageLink, details);
      container.append(line);
    }
    drawer.querySelector('[data-cart-total]').textContent = money(cart.total_price);
    drawer.querySelector('[data-drawer-checkout]').disabled = !cart.items.length;
    drawer.querySelector('[data-cart-message]').textContent = '';
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };
  const fetchCart = async () => responseJson(await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }));
  const openCart = async () => {
    if (!drawer?.showModal) { window.location.assign(config.cartUrl); return; }
    previousFocus = document.activeElement;
    if (!drawer.open) drawer.showModal();
    document.documentElement.classList.add('ot-cart-is-open');
    drawer.querySelector('[data-drawer-checkout]').disabled = true;
    drawer.querySelector('[data-cart-message]').textContent = text.loading;
    try { renderCart(await fetchCart()); }
    catch (error) { drawer.querySelector('[data-cart-message]').textContent = error.message || text.error; }
  };
  const changeCart = async (line, quantity) => {
    if (cartBusy) return;
    cartBusy = true;
    drawer.setAttribute('aria-busy', 'true');
    drawer.querySelectorAll('button, input').forEach(node => { node.dataset.previousDisabled = String(node.disabled); node.disabled = true; });
    try {
      const cart = await responseJson(await fetch(`${config.cartChangeUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.lineKey, quantity }) }));
      renderCart(cart);
    } catch (error) {
      try { renderCart(await fetchCart()); } catch {}
      drawer.querySelector('[data-cart-message]').textContent = error.message || text.error;
    } finally {
      cartBusy = false;
      drawer.removeAttribute('aria-busy');
      drawer.querySelectorAll('[data-previous-disabled]').forEach(node => { node.disabled = node.matches('[data-drawer-checkout]') ? !drawer.querySelector('.ot-drawer-line') : node.dataset.previousDisabled === 'true'; delete node.dataset.previousDisabled; });
    }
  };
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-cart-open]');
    if (open) { event.preventDefault(); openCart(); return; }
    if (event.target.closest('[data-cart-close]')) { drawer?.close(); return; }
    if (event.target === drawer) { const bounds = drawer.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) drawer.close(); return; }
    const root = event.target.closest('[data-product-root]');
    const gallery = event.target.closest('[data-gallery-to], [data-gallery-step]');
    if (root && gallery) { galleryTo(root, gallery.hasAttribute('data-gallery-to') ? Number(gallery.dataset.galleryTo) : Number(root.dataset.galleryIndex || 0) + Number(gallery.dataset.galleryStep)); return; }
    const quantityButton = event.target.closest('[data-quantity-step]');
    if (root && quantityButton) {
      const input = root.querySelector('[data-product-quantity]');
      try { Number(quantityButton.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown(); } catch {}
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const cartButton = event.target.closest('[data-cart-step], [data-cart-remove]');
    if (cartButton) {
      const line = cartButton.closest('[data-line-key]');
      const input = line.querySelector('[data-cart-quantity]');
      let quantity = cartButton.hasAttribute('data-cart-remove') ? 0 : Number(input.value) + Number(cartButton.dataset.cartStep);
      if (quantity > 0 && quantity < Number(input.min)) quantity = 0;
      if (input.max) quantity = Math.min(quantity, Number(input.max));
      changeCart(line, Math.max(0, quantity));
    }
  });
  document.addEventListener('change', event => {
    const root = event.target.closest('[data-product-root]');
    if (root && event.target.matches('[data-option-value-id], [data-option-select], [data-sticky-variant]')) selectVariant(root, event.target);
    if (event.target.matches('[data-cart-quantity]')) {
      if (event.target.checkValidity()) changeCart(event.target.closest('[data-line-key]'), Number(event.target.value));
      else event.target.reportValidity();
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('.ot-product-form');
    if (!form) return;
    const root = form.closest('[data-product-root]');
    if (event.submitter?.closest('.shopify-payment-button')) return;
    event.preventDefault();
    if (root.hasAttribute('aria-busy') || !form.reportValidity()) return;
    const data = new FormData(form);
    if (!data.get('id')) { setMessage(root, text.unavailable, true); return; }
    const checkout = event.submitter?.hasAttribute('data-buy-now');
    data.delete('return_to');
    setProductBusy(root, true);
    setMessage(root, text.loading);
    try {
      await responseJson(await fetch(`${config.cartAddUrl}.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } }));
      setMessage(root, text.added);
      if (checkout) window.location.assign('/checkout');
      else await openCart();
    } catch (error) { setMessage(root, error.message || text.error, true); }
    finally { setProductBusy(root, false); }
  });
  drawer?.addEventListener('close', () => { document.documentElement.classList.remove('ot-cart-is-open'); previousFocus?.focus(); });
  document.querySelectorAll('[data-product-root]').forEach(initializeProduct);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-product-root]').forEach(initializeProduct));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-product-root]').forEach(root => observers.get(root)?.forEach(observer => observer.disconnect())));
})();
